import { DeveloperRequestPayload } from 'common/types/developerRequests';
import config from 'config';
import api from './api';

/** Drop keys whose value is undefined or an empty string so optional
 *  questions the developer left blank are omitted from the body rather
 *  than sent as `""`. (JSON.stringify already drops `undefined`, but not
 *  empty strings.) Required fields are validated non-empty by the form. */
const compact = <T extends Record<string, unknown>>(obj: T): Partial<T> =>
  Object.fromEntries(
    Object.entries(obj).filter(
      ([, value]) => value !== undefined && value !== '',
    ),
  ) as Partial<T>;

/**
 * Submit a WHOOP API Platform request to portal-service.
 *
 * portal-service deserializes this body as snake_case (HubSpot immutables style,
 * same convention as the apps endpoints, e.g. `logo_url`), so we map the camelCase
 * payload to snake_case here. Optional fields the developer left blank are dropped.
 *
 * The server derives the requester's identity (name / company / email) entirely
 * from the bearer token, so those fields are intentionally NOT part of the body.
 */
const submitDeveloperRequest = async (
  payload: DeveloperRequestPayload,
): Promise<void> => {
  await api.emptyPost(
    `${config.apiBaseUrl}/v1/developer-request`,
    compact({
      request_type: payload.requestType,
      client_id: payload.clientId,
      intended_use: payload.intendedUse,
      ux_designs_link: payload.uxDesignsLink,
      expected_timeline: payload.expectedTimeline,
      expected_usage_details: payload.expectedUsageDetails,
      higher_rate_limit_reason: payload.higherRateLimitReason,
      higher_app_limit_reason: payload.higherAppLimitReason,
      idea_request: payload.ideaRequest,
      question: payload.question,
      requested_user_limit: payload.requestedUserLimit,
      requested_minute_rate_limit: payload.requestedMinuteRateLimit,
      requested_day_rate_limit: payload.requestedDayRateLimit,
      public_url: payload.publicUrl,
      no_public_url_reason: payload.noPublicUrlReason,
    }),
  );
};

export default {
  submitDeveloperRequest,
};
